"use client";

import { cn } from "@/lib/utils";
import { memo, useRef } from "react";
import * as images from "@/components/images/Images";
import Link from "next/link";
import Image from "next/image";
import Icon from "@/components/ui/Icon";
import { useGetBoundingClientRect } from "@/hooks/useGetBoundingClientRect";
import { useSetGlobalCustomProperty } from "@/hooks/useSetGlobalCustomProperty";
import CheckoutProgressSteps from "./components/CheckoutProgressSteps";

export type CheckoutHeaderProps = {
  isProgressBar?: boolean;
} & React.ComponentProps<"header">;

export const CheckoutHeader = ({
  className,
  isProgressBar = false,
}: CheckoutHeaderProps) => {
  const headerRef = useRef<HTMLElement>(null);
  const [headerClientRect] = useGetBoundingClientRect(headerRef);

  useSetGlobalCustomProperty(
    "--checkout-header-height",
    `${Math.round(headerClientRect?.height ?? 0)}px`
  );

  return (
    <header
      ref={headerRef}
      className={cn(
        "section w-screen flex bg-white overflow-hidden border-b border-neutral-100 fixed top-0 inset-x-0 z-10", {
            "h-14": !isProgressBar,
        },
        className
      )}
    >
      <div className="container flex flex-auto min-w-0 mx-auto">
        <div className="flex-auto min-w-0 flex items-center justify-between flex-wrap flex-nowrap md:gap-6">
          <div className="py-4 md:py-0">
            <Link href="/">
              <Image
                src={images.logo}
                alt="Languex logo"
                width={124}
                height={32}
                priority
              />
            </Link>
          </div>
          {!!isProgressBar && (
            <div className="w-full flex justify-center min-w-0 py-4 px-10 relative before:absolute before:w-screen before:top-0 before:left-1/2 before:-translate-x-1/2 before:border-t before:border-t-neutral-100 order-3 md:order-2 md:before:hidden md:w-auto md:flex-auto md:py-0">
              <CheckoutProgressSteps />
            </div>
          )}
          <div className="py-4 md:order-3 md:py-0">
            <Link
              href="/support"
              target="_blank"
              className="text-semantic-text-primary inline-flex items-center"
            >
              <Icon id="icon-smart-hint_24" className="shrink-0 w-6 h-6 mr-1" />
              <span className="text-xs">Help</span>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default memo(CheckoutHeader);
